import { Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { DomainEventBus, DomainEvent, DomainEventType } from '../events/domain-event-bus';
import { metrics, trace } from '@opentelemetry/api';
import { OTEL_SERVICE_NAME } from './telemetry.constants';
import { flushMetrics, flushTraces } from '../../../opentelemetry/init-otel';
import { logs, SeverityNumber } from '@opentelemetry/api-logs';

type FlowCtx = {
  span: import('@opentelemetry/api').Span;
  publicId: string;
  correlationId?: string;
  lastRedirectUrl?: string;
  pageReadyAt?: number;
};

@Injectable({ providedIn: 'root' })
export class PaymentFlowTelemetryService {
  private readonly tracer = trace.getTracer(OTEL_SERVICE_NAME);
  private currentFlow?: FlowCtx;
  // Metric: time from page ready to first handled status (moved from PaymentTelemetryService)
  private readonly meter = metrics.getMeter(OTEL_SERVICE_NAME);
  private readonly payFlowDuration = this.meter.createHistogram('ui_pay_flow_duration_ms', {
    description: 'Time from page ready to first handled payment status',
    unit: 'ms'
  });
  private readonly logger = logs.getLogger(OTEL_SERVICE_NAME);

  constructor(bus: DomainEventBus) {
    // Start flow on PaymentPageReady
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentPageReady }> => e.type === DomainEventType.PaymentPageReady)).subscribe((e) => {
      // End any existing flow before starting a new one
      if (this.currentFlow) {
        try {
          this.currentFlow.span.addEvent('flow.replaced');
          this.currentFlow.span.end();
        } finally {
          this.currentFlow = undefined;
        }
      }
      const span = this.tracer.startSpan('ui.pay.flow', {
        attributes: {
          'payment.public_id': e.publicId,
          'session.id': e.sessionId
        },
        startTime: e.timestamp
      });
      this.currentFlow = { span, publicId: e.publicId, pageReadyAt: e.timestamp };
      span.addEvent('page.ready', {}, e.timestamp);
    });

    // Mark submit inside the single flow
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentSubmit }> => e.type === DomainEventType.PaymentSubmit)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.correlationId = e.correlationId;
      flow.span.setAttribute('payment.correlation_id', e.correlationId);
      flow.span.addEvent('submit', { 'session.id': e.sessionId }, e.timestamp);
    });

    // PSP redirect handoff
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PspRedirectHandoff }> => e.type === DomainEventType.PspRedirectHandoff)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.lastRedirectUrl = e.url;
      try {
        const url = new URL(e.url, window.location.href);
        flow.span.addEvent('psp.redirect.handoff', { 'psp.host': url.host, 'psp.path': url.pathname, 'session.id': e.sessionId }, e.timestamp);
      } catch {
        flow.span.addEvent('psp.redirect.handoff', { 'session.id': e.sessionId }, e.timestamp);
      }
      flow.span.setAttribute('payment.outcome', 'redirect');
      flow.span.end(e.timestamp);
      this.currentFlow = undefined;
      flushTraces();
    });

    // API initiate (events on flow)
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentApiInitiateEnd }> => e.type === DomainEventType.PaymentApiInitiateEnd)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.span.addEvent('payment.api.initiate.end', { 'session.id': e.sessionId, ok: (e as any).ok === true }, e.timestamp);
    });

    // API status checks (events on flow)
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentApiStatusCheckEnd }> => e.type === DomainEventType.PaymentApiStatusCheckEnd)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.span.addEvent('payment.api.status_check.end', { 'session.id': e.sessionId, ok: (e as any).ok === true }, e.timestamp);
    });

    // SignalR lifecycle (events on flow)
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.SignalrState }> => e.type === DomainEventType.SignalrState)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.span.addEvent('signalr.state', { 'signalr.state': e.state, 'session.id': e.sessionId }, e.timestamp);
    });

    // SignalR poll iterations (events on flow)
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.SignalrPollIteration }> => e.type === DomainEventType.SignalrPollIteration)).subscribe((e) => {
      const flow = this.currentFlow;
      if (flow) flow.span.addEvent('signalr.poll.iteration', { iteration: e.iteration, 'session.id': e.sessionId }, e.timestamp);
    });

    // PaymentStatusFirstHandled no longer ends the flow; if needed, log it as an event when flow is active
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.PaymentStatusFirstHandled }> => e.type === DomainEventType.PaymentStatusFirstHandled)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.span.addEvent('status.first_handled', { 'payment.request_status': e.requestStatus, 'payment.status': e.status, 'session.id': e.sessionId }, e.timestamp);
      // Record metric if we have a page ready timestamp on the active flow
      if (flow.pageReadyAt) {
        const duration = e.timestamp - flow.pageReadyAt;
        this.payFlowDuration.record(duration);

        // Get trace ID and span ID from the flow span context
        const spanContext = flow.span.spanContext();
        const traceId = spanContext.traceId;
        const spanId = spanContext.spanId;

        this.logger.emit({
          body: 'UI pay flow duration recorded',
          severityNumber: SeverityNumber.INFO,
          severityText: 'INFO',
          attributes: {
            'session.id': e.sessionId,
            'payment.public_id': flow.publicId,
            'payment.correlation_id': flow.correlationId,
            duration_ms: duration,
            'payment.status': e.status,
            'payment.request_status': e.requestStatus,
            'trace.id': traceId,
            'span.id': spanId
          }
        });
        flow.pageReadyAt = undefined;
        // Flush metrics to increase likelihood of export before navigation changes
        void flushMetrics();
      }
    });

    // Loader visibility routed into the active payment flow
    bus.events$.pipe(filter((e): e is Extract<DomainEvent, { type: typeof DomainEventType.LoaderVisible }> => e.type === DomainEventType.LoaderVisible)).subscribe((e) => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.span.addEvent('loader.toggle', { visible: e.visible, 'session.id': e.sessionId }, e.timestamp);
      // If loader hides, flush traces so the event is exported quickly while the page is still active
      if (!e.visible) {
        flushTraces();
      }
    });

    // Before unload, annotate if redirect in progress
    window.addEventListener('beforeunload', () => {
      const flow = this.currentFlow;
      if (!flow) return;
      flow.span.addEvent('beforeunload', { reason: flow.lastRedirectUrl ? 'psp_redirect' : 'unknown' });
    });
  }
}
